class ProductMultibuy extends HTMLElement {
  // Static registry to track all instances
  static instances = new Set();

  connectedCallback() {
    this.selectedVariant = this.querySelector('.multibuy-selected-option');
    this.menu = this.querySelector('.multibuy-menu');
    this.closeButton = this.querySelector('.menu__close__button');
    this.options = this.querySelectorAll('.multibuy-option');
    this.quantityInputs = document.querySelectorAll('.multibuy-quantity-input');
    this.container = this.querySelector('.multibuy-container');
    this.isDisabled = this.container?.classList.contains('multibuy-container--disabled') || false;
    
    // Find the product card container (parent element)
    this.productCard = this.closest('product-card') || this.closest('.product-card');
    
    // Register this instance
    ProductMultibuy.instances.add(this);
    
    // Initialize with first option only if not disabled
    if (!this.isDisabled) {
      const firstOption = this.options[0];
      if (firstOption instanceof HTMLElement) {
        this.updateSelectedVariant(firstOption);
      }
    }
    
    // Toggle menu on selected variant click (only if not disabled)
    if (this.selectedVariant && !this.isDisabled) {
      this.selectedVariant.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.toggleMenu();
      });
    }
    
    // Close menu button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.closeMenu();
      });
    }
    
    // Handle option clicks
    this.options.forEach(option => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.handleOptionClick(e);
      });
    });
    
    // Add click-outside listener for closing menu
    this.setupClickOutsideListener();
  }

  disconnectedCallback() {
    // Unregister this instance when removed from DOM
    ProductMultibuy.instances.delete(this);
    // Remove click-outside listener
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
      this.clickOutsideHandler = null;
    }
  }

  setupClickOutsideListener() {
    /**
     * @param {MouseEvent} e
     */
    this.clickOutsideHandler = (e) => {
      // Only close if menu is open
      if (!this.menu || !this.menu.classList.contains('active')) {
        return;
      }
      
      // Check if click is outside the product card
      const clickedElement = e.target;
      if (this.productCard && clickedElement instanceof Node && !this.productCard.contains(clickedElement)) {
        this.closeMenu();
      }
    };
    
    // Use capture phase to ensure we catch clicks before they bubble
    document.addEventListener('click', this.clickOutsideHandler, true);
  }

  toggleMenu() {
    if (this.isDisabled || !this.menu) return;
    const isCurrentlyOpen = this.menu.classList.contains('active');
    
    // Close all other multibuy menus
    ProductMultibuy.closeAllMenus(this);
    
    // Toggle this menu
    if (!isCurrentlyOpen) {
      this.menu.classList.add('active');
    } else {
      this.menu.classList.remove('active');
    }
  }

  openMenu() {
    if (this.isDisabled || !this.menu) return;
    // Close all other multibuy menus before opening this one
    ProductMultibuy.closeAllMenus(this);
      this.menu.classList.add('active');
    }

  /**
   * Close all multibuy menus except the specified instance
   * @param {ProductMultibuy | null} exceptInstance - Instance to exclude from closing
   */
  static closeAllMenus(exceptInstance) {
    ProductMultibuy.instances.forEach(instance => {
      if (instance !== exceptInstance && instance.menu) {
        instance.closeMenu();
      }
    });
  }

  closeMenu() {
    if (this.menu) {
      this.menu.classList.remove('active');
    }
  }

  /**
   * @param {Event} e
   */
  handleOptionClick(e) {
    let option = e.currentTarget;
    if (!(option instanceof HTMLElement) && e.target instanceof Element) {
      option = e.target.closest('.multibuy-option');
    }
    if (!(option instanceof HTMLElement)) return;
    
    const quantity = parseInt(option.dataset.quantity || '1', 10);
    this.selectOption(option, quantity);
  }

  /**
   * @param {HTMLElement} option
   */
  updateSelectedVariant(option) {
    if (!this.selectedVariant || !option) return;
    
    const packSize = option.querySelector('.multibuy-option__pack-size')?.textContent || '';
    const totalPrice = option.querySelector('.multibuy-option__total-price')?.textContent || '';
    const unitPrice = option.querySelector('.multibuy-option__unit-price')?.textContent || '';
    
    // Update data attributes
    if (this.selectedVariant instanceof HTMLElement) {
      this.selectedVariant.dataset.packsize = option.dataset.quantity || '';
      this.selectedVariant.dataset.totalPrice = option.dataset.totalPrice || '';
      this.selectedVariant.dataset.unitPrice = option.dataset.unitPrice || '';
      
      // Update display
      const packSizeTextSpan = this.selectedVariant.querySelector('.multibuy-selected-option__pack-size-text');
      const totalPriceSpan = this.selectedVariant.querySelector('.multibuy-selected-option__total-price');
      const unitPriceSpan = this.selectedVariant.querySelector('.multibuy-selected-option__unit-price');
      
      if (packSizeTextSpan) packSizeTextSpan.textContent = packSize;
      if (totalPriceSpan) totalPriceSpan.textContent = totalPrice;
      if (unitPriceSpan) unitPriceSpan.textContent = unitPrice;
    }
  }

  /**
   * @param {HTMLElement} option
   * @param {number} quantity
   */
  selectOption(option, quantity) {
    // Update visual selection
    if (this.options) {
      this.options.forEach(opt => opt.classList.remove('multibuy-option--selected'));
    }
    option.classList.add('multibuy-option--selected');
    
    // Update selected variant display
    if (this.selectedVariant) {
      this.updateSelectedVariant(option);
    }
    
    // Update quantity inputs in all forms
    if (this.quantityInputs) {
      this.quantityInputs.forEach(input => {
        if (input instanceof HTMLInputElement) {
          input.value = quantity.toString();
        }
      });
    }
    
    // Close menu
    if (this.menu) {
      this.closeMenu();
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('multibuy:quantity-changed', {
      detail: { quantity },
      bubbles: true
    }));
  }
}

if (!customElements.get('product-multibuy')) {
  customElements.define('product-multibuy', ProductMultibuy);
}

